import { bindable } from 'aurelia-framework';
import { debounce, each, some } from 'lodash';

export class SideBar {
	@bindable buttonTextResource: string;
	@bindable linkToolbar = false;
	@bindable closeOnOutsideClick = true;

	sideBar: HTMLElement;
	visible = false;

	private containers: { [key: string]: ISideBarContainer } = {};
	private buttons: ISideBarButton[] = [];
	private onResize = debounce(this.syncHeight.bind(this), 200);
	private closeEventListeners: Array<() => void> = [];

	attached() {
		this.syncHeight();
		window.addEventListener('resize', this.onResize, true);
		if (this.closeOnOutsideClick) {
			document.addEventListener('click', this.onClickedOutside);
		}
	}

	private onClickedOutside = ((event: Event) => {
		const hasSideBarParent = (element: HTMLElement) => {
			if (element.className.split(' ').indexOf('side-bar') !== -1) { return true; }
			if (!element.parentElement) { return false; };
			return hasSideBarParent(element.parentElement);
		};

		if (!hasSideBarParent(event.target as HTMLElement)) {
			this.close();
		}
	}).bind(this);

	showContainer(key: string) {
		this.tryContainer(key, container => {
			if (!this.visible) {
				this.visible = true;
				each(this.containers, c => { c.visible = false; });
				container.visible = true;
			} else {
				each(this.containers, c => { c.visible = false; });
				container.visible = true;
				this.resetButtons();
			}
		});
	}

	closeSuccess() {
		this.visible = false;
		this.resetButtons();
	}

	isVisible(key: string) {
		return this.tryContainer(key, c => c.visible);
	}

	addContainer(container: ISideBarContainer) {
		this.containers[container.key] = container;
	}

	addButton(button: ISideBarButton) {
		this.buttons.push(button);
	}

	close() {
		this.visible = false;
		this.resetButtons();
		this.closeEventListeners.forEach(listener => listener());
	}

	private tryContainer(key: string, action: (container: any) => any) {
		const container = this.containers[key];
		if (!container) { return; }
		return action(container);
	}

	private resetButtons() {
		this.buttons.forEach(b => b.resetClass());
	}

	syncHeight() {
		const html = document.documentElement;
		this.sideBar.style.height = `${html.clientHeight - this.sideBar.getBoundingClientRect().top - 5}px`;
	}

	addCloseEventListener(eventHandler: () => void) {
		this.closeEventListeners = this.closeEventListeners.concat(eventHandler);
	}

	removeCloseEventListener(eventHandler: () => void) {
		this.closeEventListeners = this.closeEventListeners.filter(f => f !== eventHandler);
	}

	detached() {
		window.removeEventListener('resize', this.onResize, true);
		if (this.closeOnOutsideClick) {
			document.removeEventListener('click', this.onClickedOutside);
		}
	}
}

export interface ISideBarContainer {
	readonly key: string;
	close(): void;
	visible: boolean;
}

export interface ISideBarButton {
	resetClass(): void;
}
